import {
  type ChangeEvent,
  type ReactNode,
  useId,
  useRef,
  useState,
} from "react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

/**
 * OKS-538 — Reusable brand-image upload widget.
 *
 * Generalized from the OKS-40 `brand-logo-uploader`: a labelled file
 * input + helper line + live client-side preview + optional "Remove"
 * affordance. It is display-agnostic — the caller supplies
 * `renderPreview(url)` so the same widget drives a round brand avatar, a
 * frame-less badge image, or (OKS-539) a rank blazon without change.
 *
 * # Upload
 *
 * The `<input type="file" name={name}>` submits through the parent
 * Inertia `<Form>` — a file input in the form auto-triggers a multipart
 * request (Crew rule: Inertia `<Form>`, never shadcn Form / RHF / zod).
 * On pick, an object-URL preview shows immediately (optimistic).
 *
 * # Remove
 *
 * Two removal paths, resolved internally:
 *   - A freshly-picked (unsaved) file → the link clears the local pick
 *     and resets the input, reverting the preview to `currentUrl` (or the
 *     caller's default).
 *   - An already-saved image (no local pick, `currentUrl` present) → the
 *     link fires `onRemove` so the caller can hit its dedicated destroy
 *     endpoint (badges: `DELETE /badges/:id/image`).
 *
 * # Client pre-check
 *
 * Optional `validate(file)` runs on pick for a fast inline hint; a
 * non-null return is shown via `InputError` and the pick is discarded.
 * The server remains the source of truth — this is a UX affordance only.
 */
export interface BrandImageUploaderProps {
  /** Existing saved image URL (from an Inertia prop). `null` ⇒ default. */
  currentUrl: string | null
  /** Wire name, e.g. `badge[image]`. */
  name: string
  /** Visible field label. */
  label: string
  /** Helper line under the input (format / size hint). */
  helper: string
  /** Localized label for the remove/clear affordance. */
  removeLabel: string
  /** Accepted MIME types. Defaults to PNG + WebP. */
  accept?: string
  /** Fired when the user removes an already-SAVED image. */
  onRemove?: () => void
  /**
   * OKS-539 — Fired with the picked File AFTER a successful `validate`
   * pre-check. Lets a caller submit imperatively (e.g. the rank blazon
   * uploader `router.patch`es its dedicated endpoint on pick, since it
   * cannot nest a `<Form>` inside the atomic ladder form). Badges omit
   * this and submit through their parent Inertia `<Form>` as before.
   */
  onFilePicked?: (file: File) => void
  /** Server-side error messages (e.g. `errors.image`) to surface. */
  error?: string[]
  /** Optional client pre-check; return a message to reject the pick. */
  validate?: (file: File) => string | null | Promise<string | null>
  /** Renders the preview for the effective display URL (or default). */
  renderPreview: (url: string | null) => ReactNode
  /** Hides all affordances (read-only tier). */
  readOnly?: boolean
  testId?: string
}

export function BrandImageUploader({
  currentUrl,
  name,
  label,
  helper,
  removeLabel,
  accept = "image/png,image/webp",
  onRemove,
  onFilePicked,
  error,
  validate,
  renderPreview,
  readOnly = false,
  testId = "brand-image-uploader",
}: BrandImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [clientError, setClientError] = useState<string | null>(null)

  const clearPick = () => {
    setPreviewUrl(null)
    setClientError(null)
    if (inputRef.current) inputRef.current.value = ""
  }

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      clearPick()
      return
    }
    if (validate) {
      const message = await validate(file)
      if (message) {
        setClientError(message)
        setPreviewUrl(null)
        if (inputRef.current) inputRef.current.value = ""
        return
      }
    }
    setClientError(null)
    setPreviewUrl(URL.createObjectURL(file))
    onFilePicked?.(file)
  }

  const onRemoveClick = () => {
    if (previewUrl) {
      // Discard the unsaved pick — revert to the saved image / default.
      clearPick()
      return
    }
    // Remove the already-saved image via the caller's destroy endpoint.
    onRemove?.()
  }

  const displayUrl = previewUrl ?? currentUrl
  const canRemove =
    !readOnly &&
    (previewUrl != null || (currentUrl != null && onRemove != null))

  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center gap-3 sm:flex-row sm:items-start"
    >
      <div data-testid={`${testId}-preview`}>{renderPreview(displayUrl)}</div>

      {readOnly ? null : (
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor={inputId}>{label}</Label>
          <Input
            ref={inputRef}
            id={inputId}
            type="file"
            name={name}
            accept={accept}
            onChange={(event) => void onPick(event)}
            data-testid={`${testId}-input`}
          />
          <p className="text-muted-foreground text-xs">{helper}</p>
          {canRemove ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={onRemoveClick}
              data-testid={`${testId}-remove`}
              className="h-auto min-h-11 justify-start p-0 text-xs sm:min-h-0"
            >
              {removeLabel}
            </Button>
          ) : null}
          <InputError
            messages={clientError ? [clientError] : error}
            data-testid={`${testId}-error`}
          />
        </div>
      )}
    </div>
  )
}

export default BrandImageUploader
